import React, { useState, useEffect, useMemo } from 'react';
import { RefreshCw, Search, ChevronUp, ChevronDown, Cpu, Server } from 'lucide-react';
import * as adminService from '../services/adminService';

const AdminAcceleratorsPage = () => {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [providerFilter, setProviderFilter] = useState('all');
  const [sortField, setSortField] = useState('gpu');
  const [sortDir, setSortDir] = useState('asc');
  const [error, setError] = useState(null);

  useEffect(() => { fetchCatalog(); }, []);

  const fetchCatalog = async () => {
    try {
      const res = await adminService.getAcceleratorCatalog();
      if (res.success) setCatalog(res);
    } catch (err) {
      console.error('Failed to fetch catalog:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const res = await adminService.refreshAcceleratorCatalog();
      if (res.success) {
        setCatalog(res);
      } else {
        setError(res.error || 'Refresh failed');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setRefreshing(false);
    }
  };

  const providers = catalog?.providers ? Object.keys(catalog.providers) : [];

  const filteredRows = useMemo(() => {
    if (!catalog?.rows) return [];
    let rows = catalog.rows;

    if (providerFilter !== 'all') {
      rows = rows.filter(r => r.provider === providerFilter);
    }

    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(r =>
        r.gpu.toLowerCase().includes(q) ||
        r.instance_type?.toLowerCase().includes(q) ||
        r.region?.toLowerCase().includes(q) ||
        r.provider?.toLowerCase().includes(q)
      );
    }

    rows = [...rows].sort((a, b) => {
      let va = a[sortField];
      let vb = b[sortField];
      if (va == null) va = '';
      if (vb == null) vb = '';
      if (typeof va === 'number' && typeof vb === 'number') {
        return sortDir === 'asc' ? va - vb : vb - va;
      }
      va = String(va).toLowerCase();
      vb = String(vb).toLowerCase();
      return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });

    return rows;
  }, [catalog, search, providerFilter, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const formatPrice = (p) => {
    if (p == null || p === 0) return '-';
    return `$${Number(p).toFixed(3)}`;
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading accelerator catalog...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Accelerator Catalog</h2>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm"
        >
          <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
          <span>{refreshing ? 'Refreshing...' : 'Refresh Now'}</span>
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
          {error}
        </div>
      )}

      {/* Provider summary cards */}
      {catalog?.providers && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {providers.map(p => (
            <div key={p} className="bg-white p-4 rounded-lg shadow border">
              <div className="flex items-center space-x-2 mb-2">
                <Server size={16} className="text-purple-500" />
                <span className="font-semibold text-gray-900 capitalize">{p}</span>
              </div>
              {catalog.providers[p].error ? (
                <p className="text-xs text-red-500">{catalog.providers[p].error}</p>
              ) : (
                <div className="flex space-x-4 text-sm text-gray-600">
                  <span><Cpu size={14} className="inline mr-1" />{catalog.providers[p].gpu_types} GPU types</span>
                  <span>{catalog.providers[p].total_instances} instances</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {catalog?.fetched_at && (
        <p className="text-xs text-gray-400">
          Last fetched: {new Date(catalog.fetched_at).toLocaleString()}
          {catalog.cached && ' (cached)'}
        </p>
      )}

      {/* Filters */}
      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search GPU, instance, region..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
        <select
          value={providerFilter}
          onChange={(e) => setProviderFilter(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          <option value="all">All Providers</option>
          {providers.map(p => (
            <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
          ))}
        </select>
        <span className="text-sm text-gray-500">{filteredRows.length} results</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              {[
                { key: 'provider', label: 'Provider' },
                { key: 'gpu', label: 'GPU' },
                { key: 'count', label: 'Count' },
                { key: 'instance_type', label: 'Instance' },
                { key: 'cpu', label: 'vCPUs' },
                { key: 'memory_gb', label: 'RAM (GB)' },
                { key: 'device_memory_gb', label: 'VRAM (GB)' },
                { key: 'price', label: 'Price/hr' },
                { key: 'spot_price', label: 'Spot/hr' },
                { key: 'region', label: 'Region' },
              ].map(col => (
                <th
                  key={col.key}
                  onClick={() => handleSort(col.key)}
                  className="text-left px-3 py-2 text-xs text-gray-500 font-medium cursor-pointer hover:text-gray-700 select-none whitespace-nowrap"
                >
                  <span className="inline-flex items-center space-x-1">
                    <span>{col.label}</span>
                    <SortIcon field={col.key} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {filteredRows.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-gray-400">
                  {catalog?.rows?.length ? 'No results match your filters.' : 'No catalog data available. Click "Refresh Now" to fetch.'}
                </td>
              </tr>
            ) : (
              filteredRows.slice(0, 500).map((row, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-3 py-2 capitalize font-medium text-gray-700">{row.provider}</td>
                  <td className="px-3 py-2 font-semibold text-gray-900">{row.gpu}</td>
                  <td className="px-3 py-2 text-gray-600">{row.count || 1}</td>
                  <td className="px-3 py-2 text-gray-600 font-mono text-xs">{row.instance_type || '-'}</td>
                  <td className="px-3 py-2 text-gray-600">{row.cpu || '-'}</td>
                  <td className="px-3 py-2 text-gray-600">{row.memory_gb != null ? Math.round(row.memory_gb) : '-'}</td>
                  <td className="px-3 py-2 text-gray-600">{row.device_memory_gb != null ? Math.round(row.device_memory_gb) : '-'}</td>
                  <td className="px-3 py-2 text-green-700 font-mono">{formatPrice(row.price)}</td>
                  <td className="px-3 py-2 text-blue-600 font-mono">{formatPrice(row.spot_price)}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs">{row.region || '-'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {filteredRows.length > 500 && (
          <div className="p-3 border-t text-center text-xs text-gray-400">
            Showing first 500 of {filteredRows.length} results. Use filters to narrow down.
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAcceleratorsPage;
